import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, EMPTY, finalize, forkJoin, first, from, Observable, of, tap, timer, zip } from "rxjs";
import { last, map, mergeMap, switchMap, toArray } from "rxjs/operators";
import { FileSaveRequest, FilesWriterService, HttpDownloadError } from "../services/files-writer.service";
import { environment } from "../../../environments/environment";
import { FileUtilsService } from "../services/file-utils.service";
import { ToastrService } from "ngx-toastr";
import { v4 as uuidv4 } from 'uuid';
import { SubTaskInProgress } from "../models/task-in-progress";
import { CollectionFile } from "../models/collection-file";
import { ModelDownloadTaskInProgress, ModelDownloadTaskInProgressService } from "../services/model-download-task-in-progress.service";
import { ConfirmDialogService } from "src/app/components/shared/confirm-dialog/confirm.service";
import { SimulationDownloadService } from "./simulation-download.service";
import { ModelService } from "./model.service";
import { DownloadTasksTableDialogComponent } from "src/app/components/shared/tasks-in-progress/download-tasks-table-dialog.component";
import { MatDialog } from "@angular/material/dialog";

/** HTTP status codes considered transient — eligible for automatic retry. */
const TRANSIENT_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_CONCURRENCY = 4;

function isTransientError(err: unknown): boolean {
  if (err instanceof HttpDownloadError) return TRANSIENT_HTTP_STATUSES.has(err.status);
  return err instanceof TypeError; // network / fetch failure
}

function backoffMs(attempt: number): number {
  const base = Math.min(1000 * Math.pow(2, attempt), 30_000);
  return Math.round(base * (0.5 + Math.random() * 0.5));
}

/** Per-download session — persisted after first download so retry can reuse dir handle & file list. */
interface DownloadSession {
  /** The user-selected root FileSystemDirectoryHandle. */
  dirHandle: unknown;
  /** All FileSaveRequests originally resolved for this collection download. */
  files: FileSaveRequest[];
  collectionId: string;
  revisionId: string;
  simulationIds: string[];
  title: string;
}


@Injectable({
  providedIn: 'root'
})
export class CollectionFilesDownloaderService {

  /** One entry per collectionId — persisted so retry can reuse the same dir handle. */
  private downloadSessions = new Map<string, DownloadSession>();

  /** Set of collectionIds currently undergoing a retry run. */
  private _retryingIds = new BehaviorSubject<Set<string>>(new Set<string>());

  constructor(private filesWriterService: FilesWriterService,
    private fileUtilsService: FileUtilsService, private toastrService: ToastrService,
    public dialogService: ConfirmDialogService,
    private dialog: MatDialog,
    private simulationDownloadService: SimulationDownloadService,
    private modelService: ModelService,
    public modelDownloadTaskInProgressService: ModelDownloadTaskInProgressService,) {
  }

  downloadFiles(collectionId: string, revisionId: string, title: string, nameInMessage: string, simulationIds: string[]) {

    const options = {
      title: title,
      message: `You are about to download files for ${nameInMessage}. Please select the folder where the files will be downloaded to`,
      cancelText: "Cancel",
      confirmText: "Ok"
    };
    this.dialogService.open(options);

    this.dialogService.confirmed().pipe(last(), switchMap(confirmed => {
      if (confirmed) {
        return this.filesWriterService.getDirectoryHandleWithPermission({ mode: 'readwrite' })
          .pipe(
            switchMap(dirHandle => {
              this.modelDownloadTaskInProgressService.updateProgress(
                { Id: collectionId, Clear: true } as ModelDownloadTaskInProgress);

              this.modelDownloadTaskInProgressService.updateProgress(
                {
                  Id: collectionId,
                  DisplayTitle: `${title}`,
                  Progress: 2,
                  ParentId: '',
                  Status: ModelDownloadTaskInProgressService.TaskInProgressStatus,
                  RootId: collectionId
                } as ModelDownloadTaskInProgress);

              // Pre-populate the table immediately with file names at 0% before the slow blob-copy call
              forkJoin(simulationIds.map(id => this.modelService.getFilesForModel(id))).pipe(first()).subscribe(fileGroups => {
                const previewFiles = fileGroups.flat().filter(f => !f.path?.toLowerCase().endsWith('.manifest'));
                const seenIds = new Set<string>();
                previewFiles.forEach(f => {
                  if (seenIds.has(f.id)) return;
                  seenIds.add(f.id);
                  this.modelDownloadTaskInProgressService.updateProgress({
                    Id: f.id,
                    DisplayTitle: f.path,
                    Progress: 0,
                    ParentId: collectionId,
                    Status: ModelDownloadTaskInProgressService.TaskInProgressStatus,
                    RootId: collectionId
                  } as ModelDownloadTaskInProgress);
                });
              });

              const dialogRef = this.dialog.open(DownloadTasksTableDialogComponent, {
                width: '90vw',
                height: '90vh',
                data: collectionId,
              });

              dialogRef.afterClosed().subscribe(result => {
                console.log('The dialog was closed');
              });

              return zip(simulationIds.map(p => this.simulationDownloadService.getDownloadedFiles(collectionId, revisionId, p))).pipe(
                tap(p => {
                  this.modelDownloadTaskInProgressService.updateProgress(
                    {
                      Id: collectionId,
                      DisplayTitle: `${title}`,
                      Progress: 4,
                      ParentId: '',
                      Status: ModelDownloadTaskInProgressService.TaskInProgressStatus,
                      RootId: collectionId
                    } as ModelDownloadTaskInProgress);
                }),
                map(p => {
                  const filesToBeDownloaded = p.flat();
                  const unique = [...new Set(filesToBeDownloaded.map(item => item.id))];
                  return unique.map(p => filesToBeDownloaded.find(x => x.id === p));
                }),
                tap(p => {
                  // Update existing preview rows in-place with real blobUrl; add any new ones
                  p.forEach(x => {
                    if (!x) return;
                    this.modelDownloadTaskInProgressService.updateProgress(
                      {
                        Id: x.id,
                        DisplayTitle: x.path,
                        Progress: 0,
                        ParentId: collectionId,
                        Status: ModelDownloadTaskInProgressService.TaskInProgressStatus,
                        RootId: collectionId,
                        Data: { Url: x.blobUrl }
                      } as ModelDownloadTaskInProgress);
                  });
                }),
                map(p => [p, dirHandle])
              )
            }),
            switchMap(([filesToBeDownloaded, dirHandle]) => {
              const topUuid = uuidv4();
              const needTopFolder: boolean = this.checkNeedTopFolder(filesToBeDownloaded);
              return of(filesToBeDownloaded.map(p => {
                let filePath = this.fileUtilsService.getParentFolderPath(p.path);
                if (needTopFolder) {
                  filePath = filePath === '' ? topUuid : topUuid + FileUtilsService.PATH_SEPARATOR + filePath;
                }
                let result = ({
                  id: p.id,
                  filePath: filePath,
                  fileName: this.fileUtilsService.getFileName(p.path),
                  fileSize: p.size,
                  blobUrl: p.blobUrl
                } as FileSaveRequest);
                return result;
              }))
                .pipe(
                  switchMap((files: FileSaveRequest[]) => {
                    // Save session so retry can reuse the same dir handle and file list
                    this.downloadSessions.set(collectionId, {
                      dirHandle,
                      files,
                      collectionId,
                      revisionId,
                      simulationIds,
                      title
                    });
                    return this.fileSaveRequestMap(dirHandle, collectionId, files);
                  })
                );
            }),
            catchError(err => {
              this.modelDownloadTaskInProgressService.updateProgress({ Id: collectionId, Progress: 100, Status: ModelDownloadTaskInProgressService.TaskFailureStatus } as ModelDownloadTaskInProgress);
              this.toastrService.error('files download failed');
              console.error('error downloading files');
              return of(false);
            })
          )
      } else return EMPTY;
    })).pipe(first()).subscribe();


  }

  private checkNeedTopFolder(filesToBeDownloaded: CollectionFile[]): boolean {
    let needTopFolder: boolean = false;
    for (let dFile of filesToBeDownloaded) {
      let filePath = this.fileUtilsService.getParentFolderPath(dFile.path);
      if (filePath === '') {
        needTopFolder = true;
        break;
      }
    }
    console.log("need top folder:" + needTopFolder);
    return needTopFolder;
  }

  private fileSaveRequestMap(dirHandle: unknown, collectionId: string, files: FileSaveRequest[]): Observable<boolean> {
    return this.filesWriterService.writeFiles(dirHandle, files, (subTaskInProgress: ModelDownloadTaskInProgress) => {
      this.modelDownloadTaskInProgressService.updateProgress(subTaskInProgress);
    }).pipe(
      map((savedFiles: FileSaveRequest[]) => {
        // no matter success or failure, clean the dirHandlerMap
        this.filesWriterService.cleanDirHandlerMap();
        if (savedFiles.length === files.length) {
          this.toastrService.success(`${files.length} downloaded successfully`)
          this.modelDownloadTaskInProgressService.updateProgress({ Id: collectionId, Progress: 100, Status: ModelDownloadTaskInProgressService.TaskSuccessStatus } as ModelDownloadTaskInProgress);
          return true;
        } else if (savedFiles.length > 0) {
          this.modelDownloadTaskInProgressService.updateProgress({ Id: collectionId, Progress: 100, Status: ModelDownloadTaskInProgressService.TaskFailureStatus } as ModelDownloadTaskInProgress);
          this.toastrService.error(`${savedFiles.length}/${files.length} downloaded successfully`)
        } else {
          this.modelDownloadTaskInProgressService.updateProgress({ Id: collectionId, Progress: 100, Status: ModelDownloadTaskInProgressService.TaskFailureStatus } as ModelDownloadTaskInProgress);
          this.toastrService.error('files download failed');
        }
        return false;
      }));
  }

  // ---------------------------------------------------------------------------
  // Retry API
  // ---------------------------------------------------------------------------

  /** Emits whether a retry run is currently in-flight for the given collection. */
  isRetrying$(collectionId: string): Observable<boolean> {
    return this._retryingIds.pipe(map(ids => ids.has(collectionId)));
  }

  /**
   * Retries every file with status=failure for the given collection.
   * - Refreshes the SAS URL for each file via getOneDownLoadedFile.
   * - Exponential backoff with jitter, max 3 automatic attempts per file.
   * - Only retries transient errors (408, 429, 5xx, network). 401/403/404 are surfaced immediately.
   * - Idempotent: calling while a retry is already running is a no-op.
   */
  retryFailedDownloads(collectionId: string): void {
    // Guard against overlapping runs
    if (this._retryingIds.value.has(collectionId)) return;

    const session = this.downloadSessions.get(collectionId);
    if (!session) return;

    const failedTasks = this.modelDownloadTaskInProgressService.getFailedTasks(collectionId);
    if (failedTasks.length === 0) return;

    const failedIds = new Set(failedTasks.map(t => t.Id));
    const filesToRetry = session.files.filter(f => failedIds.has(f.id));

    const newSet = new Set(this._retryingIds.value);
    newSet.add(collectionId);
    this._retryingIds.next(newSet);

    // Reset parent task so UI shows in-progress again
    this.modelDownloadTaskInProgressService.resetForRetry(collectionId);

    from(filesToRetry).pipe(
      mergeMap(file => this.retrySingleFile(session, file, 0), RETRY_CONCURRENCY),
      toArray(),
      finalize(() => {
        const updatedSet = new Set(this._retryingIds.value);
        updatedSet.delete(collectionId);
        this._retryingIds.next(updatedSet);

        const stillFailed = this.modelDownloadTaskInProgressService.getFailedTasks(collectionId);
        if (stillFailed.length === 0) {
          this.modelDownloadTaskInProgressService.updateProgress({
            Id: collectionId, Progress: 100,
            Status: ModelDownloadTaskInProgressService.TaskSuccessStatus
          } as ModelDownloadTaskInProgress);
          this.toastrService.success('All retried files downloaded successfully');
        } else {
          this.modelDownloadTaskInProgressService.updateProgress({
            Id: collectionId, Progress: 100,
            Status: ModelDownloadTaskInProgressService.TaskFailureStatus
          } as ModelDownloadTaskInProgress);
          this.toastrService.error(`${stillFailed.length} file(s) still failed after retry`);
        }
      })
    ).subscribe();
  }

  /**
   * Retries a single file identified by fileId. Follows the same backoff / SAS-refresh rules
   * as the batch retry. No-op when a batch retry is already running for this collection.
   */
  retryFile(collectionId: string, fileId: string): void {
    if (this._retryingIds.value.has(collectionId)) return;

    const session = this.downloadSessions.get(collectionId);
    if (!session) return;

    const file = session.files.find(f => f.id === fileId);
    const task = this.modelDownloadTaskInProgressService.tasksInProgress.find(
      t => t.Id === fileId && t.Status === ModelDownloadTaskInProgressService.TaskFailureStatus
    );
    if (!file || !task) return;

    const newSet = new Set(this._retryingIds.value);
    newSet.add(collectionId);
    this._retryingIds.next(newSet);

    this.modelDownloadTaskInProgressService.resetForRetry(collectionId);

    this.retrySingleFile(session, file, 0).pipe(
      finalize(() => {
        const updatedSet = new Set(this._retryingIds.value);
        updatedSet.delete(collectionId);
        this._retryingIds.next(updatedSet);

        const stillFailed = this.modelDownloadTaskInProgressService.getFailedTasks(collectionId);
        const finalStatus = stillFailed.length === 0
          ? ModelDownloadTaskInProgressService.TaskSuccessStatus
          : ModelDownloadTaskInProgressService.TaskFailureStatus;
        this.modelDownloadTaskInProgressService.updateProgress({
          Id: collectionId, Progress: 100, Status: finalStatus
        } as ModelDownloadTaskInProgress);
      })
    ).subscribe();
  }

  /**
   * Downloads a single file with automatic SAS URL refresh and exponential backoff.
   * Returns Observable<true> on success, or Observable<true> after recording the permanent failure
   * (errors are fully handled here; the observable itself does not error out).
   */
  private retrySingleFile(
    session: DownloadSession,
    originalFile: FileSaveRequest,
    attempt: number
  ): Observable<boolean> {
    console.info(`[Retry] attempt ${attempt + 1}/${MAX_RETRY_ATTEMPTS} for file ${originalFile.id} (${originalFile.fileName})`);

    return this.simulationDownloadService.getOneDownLoadedFile(originalFile.id).pipe(
      switchMap(({ url }) => {
        const refreshedFile: FileSaveRequest = { ...originalFile, blobUrl: url };
        // Reset progress/status so the UI shows downloading again
        this.modelDownloadTaskInProgressService.resetForRetry(originalFile.id);
        return this.filesWriterService.getDirHandleForFilePath(session.dirHandle, refreshedFile.filePath).pipe(
          switchMap((dirHandle: unknown) =>
            this.filesWriterService.getWriteableStream(dirHandle, refreshedFile.fileName)
          ),
          switchMap((writeableStream: unknown) =>
            this.filesWriterService.downloadBlobsForFileAndWriteToStream(
              writeableStream,
              refreshedFile,
              task => this.modelDownloadTaskInProgressService.updateProgress(task)
            )
          )
        );
      }),
      map(() => true as boolean),
      catchError(err => {
        console.error(`[Retry] attempt ${attempt + 1} failed for file ${originalFile.id}`, err);

        // 401/403/404 — non-retryable, surface immediately
        const httpStatus = err instanceof HttpDownloadError ? err.status : null;
        const nonRetryable = httpStatus !== null && !TRANSIENT_HTTP_STATUSES.has(httpStatus);

        if (nonRetryable || attempt >= MAX_RETRY_ATTEMPTS - 1) {
          const reason = nonRetryable
            ? `HTTP ${httpStatus} (non-retryable)`
            : `Max retry attempts (${MAX_RETRY_ATTEMPTS}) exhausted`;
          console.error(`[Retry] giving up on file ${originalFile.id}: ${reason}`);
          this.modelDownloadTaskInProgressService.updateProgress({
            Id: originalFile.id,
            Progress: 100,
            Status: ModelDownloadTaskInProgressService.TaskFailureStatus,
            RootId: session.collectionId,
            ParentId: session.collectionId,
            DisplayTitle: originalFile.fileName,
            ErrorReason: reason
          } as ModelDownloadTaskInProgress);
          return of(false as boolean);
        }

        // Transient error — back off and retry
        const delay = backoffMs(attempt);
        console.info(`[Retry] backing off ${delay}ms before attempt ${attempt + 2} for ${originalFile.id}`);
        return timer(delay).pipe(
          switchMap(() => this.retrySingleFile(session, originalFile, attempt + 1))
        );
      })
    );
  }

}
